import React, { useState } from 'react';
import {
  View, StyleSheet, Text, ViewStyle, TextStyle,
} from 'react-native';
import {
  VictoryAxis, VictoryChart, VictoryLabel, VictoryVoronoiContainer,
} from 'victory';
import Colors from '../../constants/Colors';
import { axisDateLabel, ChartDataPoint, tickValSamples } from '../axisUtils';
import { alternateComparedLineColor, alternateLineColor, selectedLineColor } from '../AirQualityGraph';
import { comparisonLineColor } from '../../components/Home/HomeGraphLineSelector';
import Line from './components/Line';
import XAxis from './components/XAxis';
import Tooltip from './components/Tooltip';
import { DateRange } from '../../redux/dateRangeSlice';

const chartTheme = {
  axis: {
    stroke: 'none',
    style: {
      stroke: '#696D70',
      tickLabels: { fill: '#696D70' },
      grid: { stroke: 'none' },
    },
  },
};

interface LineGraphProps {
  title: string,
  alternateTitle?: string,
  chartData: ChartDataPoint[],
  alternateData?: ChartDataPoint[],
  comparisonData?: ChartDataPoint[],
  alternateComparisonData?: ChartDataPoint[],
  showLegend?: boolean,
  style?: ViewStyle | ViewStyle[],
  horizontalTickCount?: number,
  noTitle?: boolean,
  // eslint-disable-next-line no-unused-vars
  onClick?: (date: Date) => void,
  chartHeight?: number,
  alternateColor?: string,
  unit?: string,
  axisLabel?: string,
  axisLabelStyle?: TextStyle | TextStyle[],
  hideTimeOnTooltip?: boolean,
  domainYLimit?: number,
  noAxisLabel?: boolean,
  range?: DateRange,
  loading?: boolean,
}

const LineGraph = ({
  title, alternateTitle, chartData, alternateData, comparisonData, alternateComparisonData,
  showLegend, style, noTitle, unit, horizontalTickCount = 7, onClick, chartHeight = 540,
  alternateColor = alternateLineColor, axisLabel, axisLabelStyle, hideTimeOnTooltip, domainYLimit,
  noAxisLabel, range, loading,
}: LineGraphProps) => {
  const paddingVertical: number = 75;
  const [chartWidth, setChartWidth] = useState<number>(1000);

  const domainLimit: number = domainYLimit ?? 1.3 * Math.max(
    ...(chartData ?? []).map((point) => point.value),
    ...(alternateData ?? []).map((point) => point.value),
    ...(comparisonData ?? []).map((point) => point.value),
    ...(alternateComparisonData ?? []).map((point) => point.value),
    1,
  );

  const largerDataset = (alternateData && alternateData?.length > chartData?.length)
    ? alternateData : chartData;

  return <View>
    {!noAxisLabel && <Text style={[styles.yAxisLabel, axisLabelStyle]}>
      {axisLabel ?? unit ?? title}
    </Text>}
    <View
      style={[styles.chart, style]}
      onLayout={(e) => setChartWidth(e.nativeEvent.layout.width)}
    >
      {(title && !noTitle) ? <Text style={styles.title}>{title}</Text> : null}
      <VictoryChart
        width={chartWidth}
        height={chartHeight}
        theme={chartTheme}
        domain={{ y: [0, domainLimit] }}
        containerComponent={<VictoryVoronoiContainer
          labels={({ datum }) => datum}
          voronoiDimension='x'
          labelComponent={<Tooltip
            {...{
              chartData,
              alternateData,
              comparisonData,
              alternateComparisonData,
              title,
              unit,
              alternateTitle,
              paddingVertical,
              chartHeight,
              domainLimit,
              onClick,
              alternateColor,
              hideTimeOnTooltip,
            }}
            parentWidth={chartWidth}
          />}
        />}
        padding={{
          top: paddingVertical,
          left: 54,
          right: 32,
          bottom: paddingVertical,
        }}
      >
        {XAxis(
          tickValSamples(
            [...Array(largerDataset?.length).keys()],
            Math.min(
              horizontalTickCount,
              Math.floor(
                horizontalTickCount
                * ((largerDataset?.length ?? 1) / (comparisonData?.length ?? 1)),
              ),
            ),
          ),
          (t) => (largerDataset ? axisDateLabel(largerDataset, t, range) : ''),
          Colors.light.hereworksBlack400,
          25,
          horizontalTickCount,
        )}
        {(comparisonData || alternateComparisonData) && XAxis(
          tickValSamples(
            [...Array(comparisonData?.length).keys()],
            Math.min(
              horizontalTickCount,
              Math.floor(
                horizontalTickCount * ((comparisonData?.length ?? 1) / (chartData?.length ?? 1)),
              ),
            ),
          ),
          (t) => ((comparisonData?.length > 0 && comparisonData[t])
            ? axisDateLabel(comparisonData, t) : ''),
          Colors.light.hereworksYellow600,
          45,
          horizontalTickCount,
        )}
        <VictoryAxis
          dependentAxis
          domain={{ y: [0, domainLimit ?? 100] }}
          crossAxis={false}
          tickCount={5}
          tickFormat={(value: number) => Math.round((domainLimit > 2 ? 1 : 10) * value)
            / (domainLimit > 2 ? 1 : 10)}
          fixLabelOverlap
          style={{
            axis: { stroke: 'none' },
            tickLabels: { fontSize: 14, lineHeight: 11 },
            grid: { stroke: Colors.light.hereworksWhite500, strokeWidth: 1, pointerEvents: 'none' },
          }}
          containerComponent={<VictoryVoronoiContainer />}
          tickLabelComponent={<VictoryLabel
            style={{
              fill: Colors.light.hereworksBlack400,
              fontSize: 14,
              lineHeight: 11,
              strokeWidth: 0,
              fontFamily: 'Poppins_400Regular',
            }}
            dx={-10}
            textAnchor='end'
          />}
        />
        {chartData?.length > 0 && Line({ data: chartData, stroke: Colors.light.hereworksDarkBlue })}
        {alternateData?.length > 0 && Line({ data: alternateData, stroke: alternateColor })}
        {comparisonData?.length > 0 && Line({ data: comparisonData, stroke: comparisonLineColor })}
        {alternateComparisonData?.length > 0
          && Line({ data: alternateComparisonData, stroke: alternateComparedLineColor })}
      </VictoryChart>
      {!(chartData?.length > 0 || alternateData?.length > 0 || comparisonData?.length > 0
        || alternateComparisonData?.length > 0) && <View style={styles.descriptionContainer}>
          <Text style={styles.description}>
            {loading ? 'Loading...' : 'No data available'}
          </Text>
        </View>}
    </View>
    {showLegend && <View style={styles.legend}>
      <View style={styles.legendIndicator} />
      <Text style={styles.legendLabel}>{title}</Text>
      {alternateData && <>
        <View style={[styles.legendIndicator, { backgroundColor: alternateColor }]} />
        <Text style={styles.legendLabel}>{alternateTitle}</Text>
      </>}
      {comparisonData && <>
        <View style={[styles.legendIndicator, styles.legendIndicatorCompared]} />
        <Text style={styles.legendLabel}>{`${title} (compared)`}</Text>
      </>}
      {alternateComparisonData && <>
        <View style={[styles.legendIndicator, styles.legendIndicatorAlternateCompared]} />
        <Text style={styles.legendLabel}>{`${alternateTitle} (compared)`}</Text>
      </>}
    </View>}
  </View>;
};

const styles = StyleSheet.create({
  yAxisLabel: {
    fontSize: 14,
    lineHeight: 11,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack200,
    marginBottom: 8,
  },
  chart: {
    marginStart: -12,
  },
  title: {
    fontSize: 22,
    lineHeight: 33,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginTop: 24,
  },
  descriptionContainer: {
    position: 'absolute',
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
  },
  description: {
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
  },
  legend: {
    flexDirection: 'row',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: Colors.light.hereworksWhite500,
    paddingTop: 16,
  },
  legendIndicator: {
    width: 24,
    height: 2,
    marginEnd: 8,
    backgroundColor: selectedLineColor,
  },
  legendIndicatorCompared: {
    backgroundColor: comparisonLineColor,
  },
  legendIndicatorAlternateCompared: {
    backgroundColor: alternateComparedLineColor,
  },
  legendLabel: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    marginEnd: 24,
  },
});

export default LineGraph;
